'use client';

import { useEffect, useCallback } from 'react';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import {
  HttpReplayProvider as DesignSystemHttpReplayProvider,
  type HttpReplayData,
} from '@terra/ui-design-system';

/**
 * Global provider that listens for HTTP replay events from chat tool cards
 * and opens them in the replay tab using the shared useHttpReplay hook.
 * Also provides the design system HttpReplayProvider context for HttpEditor actions.
 */
export function HttpReplayProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { openInReplay } = useHttpReplay();

  // Callback for the design system HttpReplayProvider
  // Body is passed as-is - Electron will compact JSON before sending
  const handleSendToReplay = useCallback(
    (data: HttpReplayData) => {
      openInReplay({
        method: data.method,
        url: data.url,
        headers: data.headers,
        body: data.body,
        bodyEncoding: data.bodyEncoding,
        httpVersion: data.httpVersion,
        grpcHarDisplay: data.harEntry,
      });
    },
    [openInReplay],
  );

  useEffect(() => {
    const handleReplayEvent = (event: CustomEvent) => {
      openInReplay(event.detail);
    };

    window.addEventListener(
      'terra-replay-http-request',
      handleReplayEvent as EventListener,
    );

    return () => {
      window.removeEventListener(
        'terra-replay-http-request',
        handleReplayEvent as EventListener,
      );
    };
  }, [openInReplay]);

  return (
    <DesignSystemHttpReplayProvider onSendToReplay={handleSendToReplay}>
      {children}
    </DesignSystemHttpReplayProvider>
  );
}
